// Bottom sheet с деталями долга + write-actions: платёж, досрочка, закрытие.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Debt } from '../api/types';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { useBackButton } from '../lib/use-main-button';
import { useViewCurrency } from '../lib/view-currency';
import { ConfirmDialog } from './ConfirmDialog';
import { CorrectionSheet } from './CorrectionSheet';
import { DebtEditSheet } from './DebtEditSheet';
import { PaymentSheet } from './PaymentSheet';

type Props = {
  debt: Debt;
  onClose: () => void;
};

type SubMode = null | 'payment' | 'prepayment' | 'close' | 'correct' | 'edit';

// Стабильный fallback для сред без crypto.randomUUID (старые WebViews).
function makeIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `key-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function fmtPercent(rate: string | null | undefined): string | null {
  if (rate == null) return null;
  const n = Number.parseFloat(rate);
  if (!Number.isFinite(n) || n === 0) return null;
  return `${(n * 100).toFixed(1)}%`;
}

const MONTH_RU = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
];

// Возвращает текст и тон строки-индикатора «платёж за этот месяц».
// null — статуса нет (нет графика / долг закрыт / новый — первая оплата впереди),
// а также для status='due': пока день не настал, ничего не нужно, и плашка
// «жёлто-предупреждающая» сбивала юзера («нет просрочек, почему мне говорят
// что нужно платить»). Молчание = «всё по графику». Дата следующего платежа
// и так есть в общих полях карточки.
function currentMonthBanner(
  status: 'paid' | 'due' | 'overdue' | null | undefined,
): { text: string; tone: 'good' | 'bad' } | null {
  if (!status) return null;
  const monthNom = MONTH_RU[new Date().getMonth()] ?? '';
  if (status === 'paid') {
    return { text: `Платёж за ${monthNom} — оплачен`, tone: 'good' };
  }
  if (status === 'overdue') {
    return { text: `Платёж за ${monthNom} — просрочен`, tone: 'bad' };
  }
  return null; // status === 'due' — молчим.
}

export function DebtModal({ debt, onClose }: Props): JSX.Element {
  const { formatMoney } = useViewCurrency();
  const [sub, setSub] = useState<SubMode>(null);

  // Ключи идемпотентности — по одному на каждый тип операции, обновляются
  // ПОСЛЕ успешного запроса. Пока кнопка не отжата (ретраи при сетевом
  // сбое), повторный submit с тем же ключом не создаст нового события.
  const [paymentKey, setPaymentKey] = useState<string>(() => makeIdempotencyKey());
  const [correctKey, setCorrectKey] = useState<string>(() => makeIdempotencyKey());
  const [closeKey, setCloseKey] = useState<string>(() => makeIdempotencyKey());

  // При открытии нового подмода (sub меняется) генерим свежий ключ для него,
  // чтобы вторая поездка по тому же «+ Платёж» считалась новым платежом.
  useEffect(() => {
    if (sub === 'payment' || sub === 'prepayment') setPaymentKey(makeIdempotencyKey());
    else if (sub === 'correct') setCorrectKey(makeIdempotencyKey());
    else if (sub === 'close') setCloseKey(makeIdempotencyKey());
  }, [sub]);

  // Esc + Telegram BackButton закрывают модал, если нет открытого подмода.
  useBackButton(sub ? () => setSub(null) : onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        if (sub) setSub(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, sub]);

  const queryClient = useQueryClient();
  const toast = useToast();

  const payMut = useMutation({
    mutationFn: (args: {
      amountMinor: bigint;
      isPrepayment: boolean;
      periodYm: string | null;
      occurredAt: string;
    }) =>
      api.payDebt(
        debt.id,
        {
          amountMinor: args.amountMinor,
          isPrepayment: args.isPrepayment,
          periodYm: args.periodYm,
          occurredAt: args.occurredAt,
        },
        paymentKey,
      ),
    onSuccess: (data) => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      queryClient.invalidateQueries({ queryKey: ['projection'] });
      setSub(null);
      // Если долг закрылся — закрываем модал.
      if (data.closed) onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось записать платёж')),
  });

  const closeMut = useMutation({
    mutationFn: () => api.closeDebt(debt.id, closeKey),
    onSuccess: () => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      queryClient.invalidateQueries({ queryKey: ['projection'] });
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось закрыть долг')),
  });

  const correctMut = useMutation({
    mutationFn: ({ newBalanceMinor, note }: { newBalanceMinor: bigint; note?: string }) =>
      api.correctDebt(debt.id, newBalanceMinor, note, correctKey),
    onSuccess: (data) => {
      haptic('success');
      queryClient.invalidateQueries({ queryKey: ['debts'] });
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
      queryClient.invalidateQueries({ queryKey: ['projection'] });
      setSub(null);
      // Если коррекция уравняла остаток в 0 — закрываем модал.
      if (data.closed) onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось изменить остаток')),
  });

  const principalMinor = BigInt(debt.principal_remaining_minor);
  const monthlyMinor = BigInt(debt.monthly_payment_minor);
  const prepaySavingsMinor = BigInt(debt.prepay_savings_minor);
  const apr = fmtPercent(debt.effective_apr) ?? fmtPercent(debt.nominal_rate);

  // Прогресс-бар по долгу: paid/total proxy через payments_left × monthly.
  let progressPct: number | null = null;
  if (debt.payments_left > 0 && monthlyMinor > 0n) {
    const totalLeft = monthlyMinor * BigInt(debt.payments_left);
    const initial = principalMinor + totalLeft - principalMinor;
    if (initial > 0n) {
      const ratio = (principalMinor * 100n) / initial;
      progressPct = Math.max(0, Math.min(100, 100 - Number(ratio)));
    }
  }

  if (sub === 'payment' || sub === 'prepayment') {
    return (
      <PaymentSheet
        debt={debt}
        isPrepayment={sub === 'prepayment'}
        loading={payMut.isPending}
        onSubmit={(payload) =>
          payMut.mutate({
            amountMinor: payload.amountMinor,
            isPrepayment: sub === 'prepayment',
            periodYm: payload.periodYm,
            occurredAt: payload.occurredAt,
          })
        }
        onCancel={() => setSub(null)}
      />
    );
  }

  if (sub === 'close') {
    return (
      <ConfirmDialog
        title={`Закрыть долг?`}
        description={`«${debt.title}» — текущий остаток ${formatMoney(principalMinor, debt.currency)} будет списан как закрытый.`}
        confirmLabel="Закрыть долг"
        cancelLabel="Отмена"
        destructive
        loading={closeMut.isPending}
        onConfirm={() => closeMut.mutate()}
        onCancel={() => setSub(null)}
      />
    );
  }

  if (sub === 'correct') {
    return (
      <CorrectionSheet
        debt={debt}
        loading={correctMut.isPending}
        onSubmit={(newBalanceMinor, note) => correctMut.mutate({ newBalanceMinor, note })}
        onCancel={() => setSub(null)}
      />
    );
  }

  if (sub === 'edit') {
    return <DebtEditSheet debt={debt} onClose={() => setSub(null)} />;
  }

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">{debt.title}</div>

        <div className="sheet-stat-grid">
          <div className="sheet-stat">
            <div className="sheet-stat-label">Остаток</div>
            <div className="sheet-stat-value">
              {formatMoney(principalMinor, debt.currency)}
            </div>
          </div>
          <div className="sheet-stat">
            <div className="sheet-stat-label">Платёж/мес</div>
            <div className="sheet-stat-value">
              {formatMoney(monthlyMinor, debt.currency)}
            </div>
          </div>
          {debt.payments_left > 0 ? (
            <div className="sheet-stat">
              <div className="sheet-stat-label">Платежей осталось</div>
              <div className="sheet-stat-value">{debt.payments_left}</div>
            </div>
          ) : null}
          {apr ? (
            <div className="sheet-stat">
              <div className="sheet-stat-label">APR</div>
              <div className="sheet-stat-value">{apr}</div>
            </div>
          ) : null}
          {prepaySavingsMinor > 0n ? (
            <div className="sheet-stat">
              <div className="sheet-stat-label">Экономия при досрочке</div>
              <div className="sheet-stat-value">
                {formatMoney(prepaySavingsMinor, debt.currency)}
              </div>
            </div>
          ) : null}
        </div>

        {progressPct != null ? (
          <div className="sheet-progress">
            <div className="sheet-progress-bar">
              <div className="sheet-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="sheet-progress-label">Выплачено ~{progressPct.toFixed(0)}%</div>
          </div>
        ) : null}

        {(() => {
          const banner = currentMonthBanner(debt.current_month_status);
          if (!banner) return null;
          return <div className={`sheet-month-status tone-${banner.tone}`}>{banner.text}</div>;
        })()}

        {debt.note ? <div className="sheet-note">{debt.note}</div> : null}

        <div className="sheet-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              haptic('light');
              setSub('payment');
            }}
          >
            + Платёж
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              haptic('light');
              setSub('prepayment');
            }}
          >
            Досрочка
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              haptic('light');
              setSub('correct');
            }}
          >
            Коррекция остатка
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              haptic('light');
              setSub('edit');
            }}
          >
            Редактировать
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={() => {
              haptic('warning');
              setSub('close');
            }}
          >
            Закрыть долг
          </button>
        </div>
      </div>
    </div>
  );
}
